import webApiClient from "../api/client";

/**
 * Canonical dance styles for the web app.
 *
 * Single source of truth is the backend config, served at GET /api/v2/dance-styles.
 * This module fetches + caches that list (localStorage) and ships a bundled fallback
 * that MIRRORS the backend list exactly, so first paint / offline never shows an
 * empty or stale picker.
 *
 * Data stores the slug `id`. The UI resolves id -> label for display and
 * id -> color (hex from config) for selection chips, calendar badges, and class
 * differentiation. Colour comes from the config `color` field (NOT a hand-authored
 * --dl-color-dance-* CSS var) so adding a future style is a single backend edit.
 */

const STORAGE_KEY = "dl_dance_styles_v1";

// Bundled fallback — keep in sync with backend-R1/config/danceStyles.js (26 styles).
export const FALLBACK_DANCE_STYLES = [
    // Latin / social core
    { id: "zouk", label: "Zouk", color: "#10bfbf", icon: "dance-ballroom", aliases: ["zouk", "brazilian zouk"] },
    { id: "bachata", label: "Bachata", color: "#451f78", icon: "dance-ballroom", aliases: ["bachata"] },
    { id: "salsa", label: "Salsa", color: "#e74c3c", icon: "dance-ballroom", aliases: ["salsa"] },
    { id: "kizomba", label: "Kizomba", color: "#228b22", icon: "dance-ballroom", aliases: ["kizomba"] },
    { id: "urban-kiz", label: "Urban Kiz", color: "#3949ab", icon: "dance-ballroom", aliases: ["urban kiz", "urbankiz", "urban-kiz"] },
    { id: "semba", label: "Semba", color: "#c0392b", icon: "dance-ballroom", aliases: ["semba"] },
    { id: "tango", label: "Tango", color: "#ffd700", icon: "dance-ballroom", aliases: ["tango", "argentine tango"] },
    { id: "forro", label: "Forró", color: "#fd9644", icon: "dance-ballroom", aliases: ["forro", "forró"] },
    { id: "samba-gaf", label: "Samba (Gaf)", color: "#ff1493", icon: "dance-ballroom", aliases: ["samba", "gafieira", "samba gafieira"] },
    { id: "lambada", label: "Lambada", color: "#b22222", icon: "dance-ballroom", aliases: ["lambada"] },
    { id: "bolero", label: "Bolero", color: "#5dade2", icon: "dance-ballroom", aliases: ["bolero"] },
    { id: "merengue", label: "Merengue", color: "#ff69b4", icon: "dance-ballroom", aliases: ["merengue"] },
    { id: "mambo", label: "Mambo", color: "#8a2be2", icon: "dance-ballroom", aliases: ["mambo"] },
    { id: "reggaeton", label: "Reggaeton", color: "#32cd32", icon: "dance-ballroom", aliases: ["reggaeton", "reggaetón", "perreo"] },
    { id: "west-coast-swing", label: "West Coast Swing", color: "#00a86b", icon: "dance-ballroom", aliases: ["west coast swing", "wcs", "west coast"] },
    // broader / ballroom / studio
    { id: "hip-hop", label: "Hip Hop", color: "#4b0082", icon: "dance-ballroom", aliases: ["hip hop", "hip-hop", "hiphop"] },
    { id: "ballet", label: "Ballet", color: "#f7a8c4", icon: "dance-ballroom", aliases: ["ballet"] },
    { id: "contemporary", label: "Contemporary", color: "#5f6caf", icon: "dance-ballroom", aliases: ["contemporary"] },
    { id: "jazz", label: "Jazz", color: "#d4a017", icon: "dance-ballroom", aliases: ["jazz"] },
    { id: "tap", label: "Tap", color: "#8d6e63", icon: "dance-ballroom", aliases: ["tap"] },
    { id: "ballroom", label: "Ballroom", color: "#7b1fa2", icon: "dance-ballroom", aliases: ["ballroom"] },
    { id: "latin", label: "Latin", color: "#e8590c", icon: "dance-ballroom", aliases: ["latin"] },
    { id: "swing", label: "Swing", color: "#00897b", icon: "dance-ballroom", aliases: ["swing"] },
    { id: "blues", label: "Blues", color: "#1565c0", icon: "dance-ballroom", aliases: ["blues"] },
    { id: "flamenco", label: "Flamenco", color: "#ad1457", icon: "dance-ballroom", aliases: ["flamenco"] },
    { id: "other", label: "Other", color: "#6e6969", icon: "dance-ballroom", aliases: ["other"] },
];

// In-memory list — starts as the bundled fallback, warmed from cache, replaced on fetch.
let inMemoryStyles = FALLBACK_DANCE_STYLES;

// Warm from the localStorage cache synchronously on module load (deterministic UX).
(function warmFromCache() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed?.danceStyles) && parsed.danceStyles.length > 0) {
                inMemoryStyles = parsed.danceStyles;
            }
        }
    } catch (e) {
        // ignore corrupt cache — fall back to bundled list
    }
})();

/**
 * Normalise for matching: lowercase, strip accents, unify separators
 * (hyphen/underscore/space), trim. Mirrors the backend resolver.
 */
function normalizeStyle(input) {
    return String(input)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[\s_-]+/g, " ")
        .trim();
}

/**
 * Resolve an id, label, alias, or legacy value to a canonical id (or null).
 * Separator/case/accent-insensitive — mirrors the backend.
 */
export function resolveToId(input) {
    if (!input) return null;
    const norm = normalizeStyle(input);
    if (!norm) return null;
    const match = inMemoryStyles.find(
        s =>
            normalizeStyle(s.id) === norm ||
            normalizeStyle(s.label) === norm ||
            (s.aliases || []).some(a => normalizeStyle(a) === norm)
    );
    return match ? match.id : null;
}

/** Current canonical list (fetched-or-cached-or-fallback). Call at render time. */
export function getDanceStyles() {
    return inMemoryStyles;
}

/** Full style object by canonical id. */
export function getDanceStyleById(id) {
    return inMemoryStyles.find(s => s.id === id);
}

/** Display label for a canonical id; falls back to the input when unknown. */
export function getDanceStyleLabel(id) {
    const style = inMemoryStyles.find(s => s.id === id);
    return style ? style.label : id;
}

/**
 * Resolve an id/label/alias/legacy value to its colour (hex from config).
 * Falls back to neutral grey when unresolved. Safe to use in inline style props.
 */
export function getDanceStyleColor(idOrValue) {
    const id = resolveToId(idOrValue);
    const style = id ? inMemoryStyles.find(s => s.id === id) : null;
    return style ? style.color : "var(--dl-color-medium)";
}

/**
 * Label contrast bars, both measured against a white chip fill.
 *   below MIN      — the hue is beyond saving as text; use the standard dark label.
 *   MIN to TARGET  — readable-ish but weak; keep the hue and darken it up to TARGET.
 *   above TARGET   — use the style colour as-is.
 * TARGET is the WCAG bar for body text. MIN is deliberately lower: applying 4.5 as a
 * pass/fail cut would send 14 of the 26 styles to plain dark, so the middle band exists
 * to keep a recognisable hue wherever darkening can rescue it.
 *
 * Mirrors components/pickers/DanceStyleChips.tsx in the mobile app — same bands, same
 * darkening step — so a style reads the same on both platforms.
 */
const MIN_CONTRAST_ON_WHITE = 3;
const TARGET_CONTRAST_ON_WHITE = 4.5;

/**
 * WCAG contrast ratio of a #rrggbb hex against white.
 *
 * Perceived brightness (BT.601 luma) is the wrong tool here and misranks saturated
 * mid-tones: it scores Reggaeton's #32cd32 as darker than Bolero's #5dade2, when the
 * real contrast is 2.12 vs 2.46 — Reggaeton is the harder of the two to read. Only a
 * proper luminance ratio orders these correctly.
 *
 * Anything the config can hold is a 6-digit hex; an unexpected value returns a passing
 * ratio so the label keeps its colour rather than silently going grey.
 */
function contrastOnWhite(hex) {
    const match = /^#?([0-9a-f]{6})$/i.exec(hex || "");
    if (!match) return MIN_CONTRAST_ON_WHITE;
    const int = parseInt(match[1], 16);
    const channel = c => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    const luminance =
        0.2126 * channel((int >> 16) & 255) +
        0.7152 * channel((int >> 8) & 255) +
        0.0722 * channel(int & 255);
    return 1.05 / (luminance + 0.05);
}

function rgbToHex(r, g, b) {
    return "#" + [r, g, b].map(c => Math.round(c).toString(16).padStart(2, "0")).join("");
}

/**
 * Darken a hex until it clears `target` contrast on white, for label text only.
 *
 * Scales all three channels by the same factor, which drops brightness while holding
 * the ratios between them — so the hue survives and only the shade moves (West Coast
 * Swing's #00a86b becomes a deeper #008353, still unmistakably its green). Chip borders
 * and selected fills keep the true config colour; this affects the label alone, so the
 * palette on screen is still the real one.
 */
const darkenedLabelCache = new Map();

function darkenForLabel(hex, target) {
    const cached = darkenedLabelCache.get(hex);
    if (cached) return cached;

    const match = /^#?([0-9a-f]{6})$/i.exec(hex || "");
    if (!match) return hex;
    const int = parseInt(match[1], 16);
    let r = (int >> 16) & 255;
    let g = (int >> 8) & 255;
    let b = int & 255;

    // Step down in small multiplicative decrements. Bounded so a pathological colour
    // can never spin here; anything that has not cleared the bar by then is near-black
    // and reads fine regardless.
    for (let i = 0; i < 40 && contrastOnWhite(rgbToHex(r, g, b)) < target; i++) {
        r *= 0.92;
        g *= 0.92;
        b *= 0.92;
    }

    const result = rgbToHex(r, g, b);
    darkenedLabelCache.set(hex, result);
    return result;
}

/**
 * Colour for a style's label when it sits on a white background — an unselected chip.
 * Returns the style's own colour where that is legible, a darkened shade of it where
 * darkening rescues it, and the standard dark text colour where the hue cannot carry
 * text at all. Selected chips are white-on-colour and do not call this.
 *
 * Results are cached: the palette is a fixed 26 entries, so every style resolves once
 * for the life of the page rather than on each render.
 */
export function getDanceStyleLabelColor(idOrValue) {
    const color = getDanceStyleColor(idOrValue);
    if (!/^#?[0-9a-f]{6}$/i.test(color)) return "var(--dl-color-text-primary)";
    if (contrastOnWhite(color) < MIN_CONTRAST_ON_WHITE) return "var(--dl-color-text-primary)";
    return darkenForLabel(color, TARGET_CONTRAST_ON_WHITE);
}

/**
 * Fetch the canonical list from the backend, update the in-memory list, and cache
 * to localStorage. Falls back to cache/bundled on any error. Call once on app init.
 */
export async function loadDanceStyles() {
    try {
        const res = await webApiClient.get("/api/v2/dance-styles");
        const list = res?.data?.danceStyles;
        if (Array.isArray(list) && list.length > 0) {
            inMemoryStyles = list;
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ danceStyles: list, ts: Date.now() }));
            } catch (e) {
                // storage full / disabled — keep the in-memory update
            }
        }
        return inMemoryStyles;
    } catch (e) {
        console.warn("loadDanceStyles: fetch failed, using cached/bundled list:", e?.message);
        return inMemoryStyles;
    }
}

// Back-compat named export for any consumer expecting a static array.
// Prefer getDanceStyles() so fetched updates are picked up.
export const danceStyles = FALLBACK_DANCE_STYLES;
